import { Link } from 'react-router-dom';
import { useState, FormEvent } from 'react';
import SEOMeta from '@/components/feature/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9u3hqt3pcjqs2fcsla0';

const AboutPage = () => {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 5000);
      return;
    }
    formData.delete('phone_alt');

    setFormStatus('submitting');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const serverMsg = (parsed?.meta as Record<string, string>)?.message || (parsed?.message as string) || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && (parsed?.code === 'OK') && !isSpam) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于极速高清',
    description: '极速高清在线观看平台品牌介绍、口碑声誉与联系方式',
    url: 'https://hfhwpsm.cn/about',
    about: {
      '@type': 'Organization',
      name: '极速高清',
      foundingDate: '2020-01-01',
      description: '极速高清是领先的在线视频流媒体服务平台',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+86-400-888-0000',
        contactType: 'customer service',
        areaServed: 'CN',
        availableLanguage: 'Chinese',
      },
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hfhwpsm.cn' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://hfhwpsm.cn/about' },
    ],
  };

  const milestones = [
    { year: '2020', title: '品牌创立', desc: '极速高清在线观看平台正式上线，以"无需下载、即开即看"为核心理念进入市场。' },
    { year: '2021', title: '用户突破百万', desc: '凭借流畅的在线观看体验和丰富的正版内容，注册用户迅速突破100万大关。' },
    { year: '2022', title: '4K超清上线', desc: '全面升级技术架构，成为国内首批支持4K HDR在线观看的视频平台之一。' },
    { year: '2023', title: '内容生态成型', desc: '与超过200家内容制作方建立合作，内容库突破10,000部，覆盖六大品类。' },
    { year: '2024', title: '用户突破5000万', desc: '活跃用户突破5000万，日均播放时长超过2亿小时，稳居行业前列。' },
    { year: '2025', title: 'AI智能推荐', desc: '推出基于深度学习的智能推荐系统，个性化在线观看体验获得用户一致好评。' },
    { year: '2026', title: '全球化布局', desc: '启动海外市场拓展计划，将极速高清的在线观看体验带给全球更多用户。' },
  ];

  const testimonials = [
    {
      name: '张先生',
      role: '资深影迷',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Chinese%20man%20in%20his%2030s%2C%20warm%20lighting%2C%20simple%20background%2C%20friendly%20expression&width=80&height=80&seq=avatar-zhang&orientation=squarish',
      text: '用了极速高清快三年了，最满意的就是画质和流畅度。4K在线观看完全不卡顿，比下载到本地看还方便！',
    },
    {
      name: '李女士',
      role: '追剧达人',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Chinese%20woman%20in%20her%2020s%2C%20warm%20lighting%2C%20simple%20background%2C%20friendly%20expression&width=80&height=80&seq=avatar-li&orientation=squarish',
      text: '作为追剧党，极速高清的资源更新速度真的很快，热门剧集同步更新。而且手机在线观看的体验特别好，通勤路上追剧神器。',
    },
    {
      name: '王先生',
      role: '学生用户',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Chinese%20young%20man%2C%20warm%20lighting%2C%20simple%20background%2C%20friendly%20expression&width=80&height=80&seq=avatar-wang&orientation=squarish',
      text: '学生党最关心的免费内容很多！纪录片频道是我最喜欢的，学到了很多课外知识。不用下载App这点太友好了，宿舍网速也能流畅在线观看。',
    },
  ];

  return (
    <>
      <SEOMeta
        title="关于我们 - 极速高清在线观看平台 | 品牌介绍"
        description="极速高清在线观看平台品牌介绍。了解我们的发展历程、口碑声誉与联系方式。致力于为用户提供最流畅的在线观看体验。"
        keywords="极速高清,在线观看平台,关于我们,品牌介绍,联系方式"
        canonicalUrl="https://hfhwpsm.cn/about"
        ogType="website"
        schemaData={[aboutSchema, breadcrumbSchema]}
      />

      <Navbar />

      <main className="pt-16">
        <div className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20office%20workspace%20with%20warm%20lighting%20and%20team%20collaboration%2C%20creative%20atmosphere%2C%20artistic%20minimal%20style%2C%20soft%20natural%20light%2C%20elegant%20composition&width=1800&height=500&seq=about-hero&orientation=landscape"
            alt="关于极速高清"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-background-50"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-6 -mt-20 relative z-10">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors no-underline">首页</Link>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <span className="text-foreground-950 font-medium">关于我们</span>
          </nav>
        </div>

        <section className="max-w-4xl mx-auto px-4 md:px-6 pb-16">
          <div className="text-center mb-14">
            <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 mb-4">
              关于
              <span className="text-primary-500">极速高清</span>
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              极速高清成立于2020年，是国内领先的在线视频流媒体服务平台。
              我们以"让每一次在线观看都极致流畅"为使命，持续为亿万用户提供高品质的视频内容服务。
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            <div className="lg:col-span-2">
              <h2 className="text-xl font-heading text-foreground-950 mb-6">品牌故事</h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                <p>
                  极速高清的创始团队来自国内顶尖互联网公司，拥有十余年的视频技术研发经验。创始人在一次长途飞行中发现，
                  即使在飞机上有WiFi，下载视频仍需等待很久——这让他萌生了打造一个<strong>无需下载、打开即看</strong>的在线观看平台的想法。
                </p>
                <p>
                  2020年，极速高清正式上线。我们首创了"纯网页端在线观看"模式，用户无需下载任何App或插件，
                  打开浏览器就能享受高清流畅的在线观看体验。这一创新让极速高清迅速获得了大量用户的青睐。
                </p>
                <p>
                  经过多年的技术积累和内容生态建设，极速高清已经从单一的影视在线观看平台，发展成为涵盖电影、电视剧、综艺、纪录片、动漫、少儿等
                  <strong>六大内容品类</strong>的综合视频服务平台。我们与超过200家国内外内容制作方建立了深度合作关系，
                  内容库规模超过12,000部，累计服务超过5,000万活跃用户。
                </p>
              </div>
            </div>

            <div className="bg-background-100 rounded-2xl p-6 border border-background-200/60">
              <h3 className="text-lg font-heading text-foreground-950 mb-4">核心数据</h3>
              <div className="space-y-4">
                {[
                  { value: '5000万+', label: '活跃用户' },
                  { value: '12,000+', label: '内容资源' },
                  { value: '200+', label: '合作伙伴' },
                  { value: '99.9%', label: '服务可用率' },
                  { value: '<50ms', label: '平均延迟' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 border-b border-background-200/60 last:border-0">
                    <span className="text-sm text-foreground-500">{item.label}</span>
                    <span className="text-base font-bold text-primary-500 font-heading">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-16">
            <h2 className="text-xl font-heading text-foreground-950 mb-8 text-center">发展历程</h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>
              <div className="space-y-8">
                {milestones.map((item, index) => (
                  <div
                    key={index}
                    className={`relative flex items-start gap-6 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="hidden md:flex-1"></div>
                    <div className="absolute left-4 md:left-1/2 w-4 h-4 bg-primary-500 rounded-full border-4 border-background-50 -translate-x-1/2 mt-1.5 z-10"></div>
                    <div className="flex-1 ml-12 md:ml-0 md:px-0">
                      <div className={`bg-background-50 rounded-xl p-5 border border-background-200/60 ${
                        index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'
                      }`}>
                        <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-bold mb-2">
                          {item.year}
                        </span>
                        <h3 className="text-base font-heading text-foreground-950 mb-1.5">{item.title}</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-16">
            <h2 className="text-xl font-heading text-foreground-950 mb-8 text-center">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((item, index) => (
                <div key={index} className="bg-background-50 rounded-xl p-6 border border-background-200/60">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="w-12 h-12 rounded-full object-cover"
                      loading="lazy"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-950">{item.name}</p>
                      <p className="text-xs text-foreground-500">{item.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed italic">&ldquo;{item.text}&rdquo;</p>
                  <div className="flex items-center gap-0.5 mt-3 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-background-100 rounded-2xl p-8 md:p-10 border border-background-200/60">
            <h2 className="text-xl font-heading text-foreground-950 mb-6 text-center">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-500">
                  <i className="ri-mail-line text-2xl"></i>
                </div>
                <h3 className="text-base font-medium text-foreground-950 mb-1">电子邮箱</h3>
                <p className="text-sm text-foreground-500">service@jisugaoging.com</p>
                <p className="text-xs text-foreground-400 mt-1">商务合作：bd@jisugaoging.com</p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-accent-100 text-accent-500">
                  <i className="ri-phone-line text-2xl"></i>
                </div>
                <h3 className="text-base font-medium text-foreground-950 mb-1">客服热线</h3>
                <p className="text-sm text-foreground-500">400-888-0000</p>
                <p className="text-xs text-foreground-400 mt-1">周一至周日 9:00-21:00</p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-secondary-100 text-secondary-500">
                  <i className="ri-map-pin-line text-2xl"></i>
                </div>
                <h3 className="text-base font-medium text-foreground-950 mb-1">公司地址</h3>
                <p className="text-sm text-foreground-500">北京市朝阳区科技园区</p>
                <p className="text-xs text-foreground-400 mt-1">中关村软件园A座</p>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/60">
              <form className="max-w-lg mx-auto" data-readdy-form="true" onSubmit={handleFormSubmit}>
                <h3 className="text-base font-heading text-foreground-950 mb-5 text-center">发送消息</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="about-name"
                        name="name"
                        placeholder="请输入姓名"
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/60 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="about-email"
                        name="email"
                        placeholder="请输入邮箱"
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/60 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="about-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      id="about-subject"
                      name="subject"
                      placeholder="请输入主题"
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/60 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息</label>
                    <textarea
                      id="about-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      placeholder="请输入消息内容（最多500字）"
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/60 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    ></textarea>
                  </div>
                  <div className="form-anti-spider">
                    <label htmlFor="phone-alt-about">备用电话</label>
                    <input type="text" id="phone-alt-about" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 text-center mt-3">消息已成功发送，我们会尽快回复您！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center mt-3">{formError}</p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default AboutPage;